import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Add, Group } from "@mui/icons-material";
import { Autocomplete, Grid, TextField } from "@mui/material";
import { GlobalBox } from "../../components/Box/GlobalBox";
import CardTable from "../../components/CardTable/CardTable";
import RegularButton from "../../components/CustomButtons/Button";
import { ModalV2 } from "../../components/Modal/ModalV2";
import Snackbars from "../../components/Snackbar/Snackbar.js";
import { FormCoverageGroupEdit } from "./FormCoverageGroupEdit";
import { columns } from "./columns";
import { COVERAGE_GROUPS_PHRASE } from "./config";
import {
  fetchCoverageGroups,
  addCoverageGroupMode,
  deleteCoverageGroup,
  clearCoverageGroups
} from "../../redux/actions/coverageGroups/coverageGroupsActions";
import { useSearch } from "../../hooks/useSearch";

const LOADINGS = [
  "fetchCoverageGroups",
];

export const CoverageGroupsPage = () => {
  const dispatch = useDispatch();
  const {
    setSnackbar,
    clearSnackbar,
    buildCompLoadings
  } = useSearch();
  const { stationsList } = useSelector(state => state.companies);
  const { addMode, coverageGroups, coverageGroupSelected, addCoverageGroup } = useSelector(state => state.coverageGroups);
  const { color, message, isOpen: isSnackbarOpen, isLoadingComp } = useSelector((state) => state.search);
  const [openDelete, setOpenDelete] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const getCoverageGroups = useCallback(
    stationID => {
      dispatch(fetchCoverageGroups(stationID));
    },
    [dispatch]
  );

  const coverageGroupsMemo = useMemo(
    () =>
      coverageGroups.map(group => {
        return {
          ...group,
          modal: setOpenModal,
          deleteModal: setOpenDelete
        };
      }),
    [coverageGroups]
  );

  const handleChangeStation = (v) => {
    const value = v ? v : null;
    setSelectedStation(value);
  };

  const handleSubmit = useCallback(() => {
    if (!selectedStation || !selectedStation.id) {
      setSnackbar(COVERAGE_GROUPS_PHRASE.search.messages.noStations);
      return;
    }
    const stationID = selectedStation.id;
    getCoverageGroups(stationID);
    setSubmitted(true);
  }, [selectedStation, setSnackbar, getCoverageGroups]);

  const handleDelete = () => {
    dispatch(deleteCoverageGroup(coverageGroupSelected.id, setOpenDelete));
  };

  useEffect(() => {
    buildCompLoadings(LOADINGS);
  }, [buildCompLoadings]);

  // Limpiar el estado al desmontar el componente
  useEffect(() => {
    return () => {
      dispatch(clearCoverageGroups());
    };
  }, [dispatch]);

  useEffect(() => {
    const isEmpty = coverageGroupsMemo.length === 0;
    if (isEmpty && submitted) {
      setSnackbar("No se encontraron agrupaciones de coberturas");
    }
    setSubmitted(false);
    // eslint-disable-next-line
  }, [coverageGroupsMemo]);

  const handleOpenAdd = () => {
    dispatch(addCoverageGroupMode());
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  return (
    <React.Fragment>
      <GlobalBox
        icon={<Group />}
        title={COVERAGE_GROUPS_PHRASE.page.table.title}
        controls={
          addCoverageGroup && (
            <RegularButton
              round
              justIcon
              color="info"
              style={{ margin: 10 }}
              onClick={handleOpenAdd}
              disabled={!selectedStation || !selectedStation.id}
            >
              <Add style={{ margin: 0 }}/>
            </RegularButton>
          )
        }
      >
        <Grid container spacing={2} sx={{marginTop: 2}}>
          <Grid item xs={12} sm={12} md={10} lg={10} xl={10}>
            <Autocomplete
              filterSelectedOptions
              options={stationsList}
              getOptionLabel={(station) => station.label}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name="station"
                  value={selectedStation}
                  label="Buscar por planta"
                  variant="outlined"
                />
              )}
              fullWidth
              onChange={(e, v) => handleChangeStation(v)}
              noOptionsText={"No hay resultados"}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
            <RegularButton
              disabled={isLoadingComp?.fetchCoverageGroups}
              onClick={handleSubmit}
              color='info'
              style={{
                height: '100%',
                margin: 0
              }}
              fullWidth
            >
              Buscar
            </RegularButton>
          </Grid>
          <Grid item xs={12}>
            <CardTable
              data={coverageGroupsMemo}
              columns={columns}
              isLoading={isLoadingComp?.fetchCoverageGroups}
              blockBtn
            />
          </Grid>
        </Grid>
      </GlobalBox>
      <ModalV2
        open={openModal}
        onClose={handleClose}
        toRight
        title={addMode ? COVERAGE_GROUPS_PHRASE.modal.titles.add : COVERAGE_GROUPS_PHRASE.modal.titles.edit}
      >
        {
          addMode
            ? <FormCoverageGroupEdit
                handleClose={handleClose}
                add
                id_station={selectedStation?.id}
                station_name={selectedStation?.label}
              />
            : <FormCoverageGroupEdit
                handleClose={handleClose}
                id_station={selectedStation?.id}
                station_name={selectedStation?.label}
              />
        }
      </ModalV2>
      <ModalV2
        title={COVERAGE_GROUPS_PHRASE.modal.titles.delete}
        justConfirm
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        minHeight={2}
        textAlign="center"
        textMargin={28}
      >
        <RegularButton
          color="transparent"
          onClick={() => setOpenDelete(false)}
        >
          {COVERAGE_GROUPS_PHRASE.modal.actions.cancel}
        </RegularButton>
        <RegularButton
          color="success"
          onClick={handleDelete}
        >
          {COVERAGE_GROUPS_PHRASE.modal.actions.delete}
        </RegularButton>
      </ModalV2>

      <Snackbars
        place="tr"
        color={color}
        message={message}
        open={isSnackbarOpen}
        closeNotification={clearSnackbar}
        close
      />
    </React.Fragment>
  );
};
